import type { Mat4 } from "./math.js";

export interface WorldRay {
  readonly origin: readonly [number, number, number];
  readonly direction: readonly [number, number, number];
  readonly length: number;
}
export interface CanvasBounds { readonly left: number; readonly top: number; readonly width: number; readonly height: number }

/** Invert the actual view-projection only on pointer selection, never at frame frequency. */
export function screenRay(clientX: number, clientY: number, bounds: CanvasBounds, viewProjection: Mat4): WorldRay | null {
  if (![clientX, clientY, bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite)
    || bounds.width <= 0 || bounds.height <= 0 || clientX < bounds.left || clientY < bounds.top
    || clientX > bounds.left + bounds.width || clientY > bounds.top + bounds.height) return null;
  const matrix = Array.from({ length: 4 }, (_, row) => Array.from({ length: 8 }, (_, col) =>
    col < 4 ? viewProjection[col * 4 + row]! : Number(col - 4 === row)));
  for (let col = 0; col < 4; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < 4; row += 1) if (Math.abs(matrix[row]![col]!) > Math.abs(matrix[pivot]![col]!)) pivot = row;
    if (!Number.isFinite(matrix[pivot]![col]) || Math.abs(matrix[pivot]![col]!) < 1e-10) return null;
    [matrix[col], matrix[pivot]] = [matrix[pivot]!, matrix[col]!];
    const divisor = matrix[col]![col]!;
    for (let index = 0; index < 8; index += 1) matrix[col]![index] = matrix[col]![index]! / divisor;
    for (let row = 0; row < 4; row += 1) {
      if (row === col) continue;
      const factor = matrix[row]![col]!;
      for (let index = 0; index < 8; index += 1) matrix[row]![index] = matrix[row]![index]! - factor * matrix[col]![index]!;
    }
  }
  const x = (clientX - bounds.left) / bounds.width * 2 - 1;
  const y = 1 - (clientY - bounds.top) / bounds.height * 2;
  const unproject = (z: number): [number, number, number] => {
    const component = (row: number): number => matrix[row]![4]! * x + matrix[row]![5]! * y + matrix[row]![6]! * z + matrix[row]![7]!;
    const w = component(3);
    return [component(0) / w, component(1) / w, component(2) / w];
  };
  const origin = unproject(-1);
  const far = unproject(1);
  const length = Math.hypot(far[0] - origin[0], far[1] - origin[1], far[2] - origin[2]);
  if (!origin.every(Number.isFinite) || !far.every(Number.isFinite) || length <= 0) return null;
  return { origin, direction: [(far[0] - origin[0]) / length, (far[1] - origin[1]) / length, (far[2] - origin[2]) / length], length };
}

export function raySphereDistance(ray: WorldRay, x: number, y: number, z: number, radius: number): number | null {
  if (![x, y, z, radius].every(Number.isFinite) || radius <= 0) return null;
  const dx = ray.origin[0] - x, dy = ray.origin[1] - y, dz = ray.origin[2] - z;
  const b = dx * ray.direction[0] + dy * ray.direction[1] + dz * ray.direction[2];
  const c = dx * dx + dy * dy + dz * dz - radius * radius;
  const discriminant = b * b - c;
  if (discriminant < 0) return null;
  const near = -b - Math.sqrt(discriminant), far = -b + Math.sqrt(discriminant);
  const distance = near >= 0 ? near : far;
  return distance >= 0 && distance <= ray.length ? distance : null;
}

/** Terrain occlusion uses bounded CPU samples on clicks, with no GPU readback. */
export function terrainOccludesRay(ray: WorldRay, hitDistance: number, heightAt: (x: number, z: number) => number): boolean {
  const steps = Math.min(256, Math.ceil(hitDistance));
  for (let index = 1; index < steps; index += 1) {
    const distance = hitDistance * index / steps;
    const x = ray.origin[0] + ray.direction[0] * distance;
    const y = ray.origin[1] + ray.direction[1] * distance;
    const z = ray.origin[2] + ray.direction[2] * distance;
    if (heightAt(x, z) > y + 0.15) return true;
  }
  return false;
}
